/**
 * Gradienta Explore page.
 * - Spectrum ([data-gx-spectrum]): choosing a column shows that artwork in the
 *   preview; Warmer / Cooler step along; "Close in colour" lists the two
 *   artworks either side. Artwork details come from each column's data-*.
 * - All artworks ([data-gx-grid]): colour and place filters, sort, count,
 *   empty state, and "Show all" on phones after the first few.
 */
(function () {
  'use strict';

  // Words from the spectrum section's data-gr-i18n="explore" block, decoded from the t filter's HTML escaping;
  // English when it's missing.
  const ENTITIES = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&#x27;': "'" };
  function words() {
    const t = { position: '{current} of {total}' };
    try {
      const got = JSON.parse(document.querySelector('[data-gr-i18n="explore"]')?.textContent || '{}');
      Object.keys(got).forEach((k) => {
        if (typeof got[k] === 'string') t[k] = got[k].replace(/&(?:amp|lt|gt|quot|#39|#x27);/g, (m) => ENTITIES[m]);
      });
    } catch (e) { /* English */ }
    return t;
  }

  function fmt(str, vars) {
    return str.replace(/\{(\w+)\}/g, (m, k) => (Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m));
  }

  function el(tag, cls, text) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text) e.textContent = text;
    return e;
  }

  function initSpectrum(root) {
    const cols = Array.from(root.querySelectorAll('[data-gx-col]'));
    if (!cols.length) return;
    const track = root.querySelector('[data-gx-track]');
    const q = (s) => root.querySelector(s);
    const img = q('[data-gx-img]');
    const series = q('[data-gx-series]');
    const name = q('[data-gx-name]');
    const where = q('[data-gx-where]');
    const swatches = q('[data-gx-swatches]');
    const view = q('[data-gx-view]');
    const price = q('[data-gx-price]');
    const wall = q('[data-gx-wall]');
    const pos = q('[data-gx-pos]');
    const near = q('[data-gx-near]');
    const T = words();
    let i = cols.findIndex((c) => c.getAttribute('aria-pressed') === 'true');
    if (i < 0) i = Math.floor(cols.length / 2);

    function keepVisible(col) {
      if (!track || track.scrollWidth <= track.clientWidth) return;
      const left = col.offsetLeft - track.offsetLeft;
      const target = left - (track.clientWidth - col.offsetWidth) / 2;
      track.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }

    function renderNear() {
      if (!near) return;
      near.textContent = '';
      [-2, -1, 1, 2].forEach((d) => {
        const k = (i + d + cols.length) % cols.length;
        const c = cols[k].dataset;
        const b = el('button', 'gx-near__item');
        b.type = 'button';
        const thumb = el('span', 'gx-near__thumb');
        const t = document.createElement('img');
        t.src = c.img.replace('width=1000', 'width=200');
        t.alt = '';
        t.loading = 'lazy';
        thumb.appendChild(t);
        const text = el('span');
        text.appendChild(el('span', 'gx-near__name', c.name));
        const pal = el('span', 'gx-near__pal');
        c.colors.split(',').forEach((hex) => { const s = el('i'); s.style.background = hex; pal.appendChild(s); });
        text.appendChild(pal);
        b.append(thumb, text);
        b.addEventListener('click', () => show(k, true));
        near.appendChild(b);
      });
    }

    function show(n, fromNear) {
      i = (n + cols.length) % cols.length;
      cols.forEach((c, k) => c.setAttribute('aria-pressed', String(k === i)));
      const d = cols[i].dataset;
      if (img) { img.removeAttribute('srcset'); img.src = d.img; img.alt = d.name; }
      if (series) series.textContent = d.series;
      if (name) name.textContent = d.name;
      if (where) { where.textContent = d.country || ''; where.hidden = !d.country; }
      if (swatches) {
        swatches.textContent = '';
        d.colors.split(',').forEach((hex) => {
          const w = el('div', 'gx-swatch');
          const s = el('i'); s.style.background = hex;
          w.append(s, el('span', '', hex));
          swatches.appendChild(w);
        });
      }
      if (view) view.href = d.url;
      if (price) price.textContent = d.price;
      if (wall) wall.href = d.url + '#cg-wall';
      if (pos) pos.textContent = fmt(T.position, { current: i + 1, total: cols.length });
      renderNear();
      keepVisible(cols[i]);
      if (fromNear) cols[i].focus({ preventScroll: true });
    }

    cols.forEach((c, k) => c.addEventListener('click', () => show(k)));
    const prev = q('[data-gx-prev]');
    const next = q('[data-gx-next]');
    if (prev) prev.addEventListener('click', () => show(i - 1));
    if (next) next.addEventListener('click', () => show(i + 1));
    show(i);
  }

  function initGrid(root) {
    const list = root.querySelector('[data-gx-list]');
    const cards = Array.from(root.querySelectorAll('[data-gx-card]'));
    if (!list || !cards.length) return;
    const famBtns = Array.from(root.querySelectorAll('[data-gx-fam]'));
    const subjBtns = Array.from(root.querySelectorAll('[data-gx-subj]'));
    const sortBtns = Array.from(root.querySelectorAll('[data-gx-sort]'));
    const count = root.querySelector('[data-gx-count]');
    const empty = root.querySelector('[data-gx-empty]');
    const clears = Array.from(root.querySelectorAll('[data-gx-clear]'));
    const more = root.querySelector('[data-gx-more]');
    const limit = parseInt(root.dataset.show, 10) || 10;
    const state = { fam: 'all', subj: 'all', sort: 'featured', all: false };
    const has = (val, id) => (' ' + val + ' ').indexOf(' ' + id + ' ') > -1;

    function press(btns, attr, value) {
      btns.forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute(attr) === value)));
    }

    function apply() {
      let shown = cards.filter((c) => (state.fam === 'all' || has(c.dataset.fam, state.fam)) && (state.subj === 'all' || has(c.dataset.subj, state.subj)));
      if (state.sort === 'hue') shown.sort((a, b) => (+a.dataset.hue) - (+b.dataset.hue) || a.dataset.name.localeCompare(b.dataset.name));
      else if (state.sort === 'az') shown.sort((a, b) => a.dataset.name.localeCompare(b.dataset.name));
      else shown.sort((a, b) => (+b.dataset.best) - (+a.dataset.best) || (+a.dataset.idx) - (+b.dataset.idx));
      cards.forEach((c) => { c.hidden = true; c.classList.remove('is-extra'); });
      shown.forEach((c, k) => { list.appendChild(c); c.hidden = false; c.classList.toggle('is-extra', k >= limit); });
      const collapsed = !state.all && shown.length > limit;
      root.classList.toggle('is-collapsed', collapsed);
      if (more) { more.hidden = !collapsed; more.textContent = (more.dataset.template || '').replace('[count]', shown.length); }
      if (count) count.textContent = (count.dataset.template || '').replace('[shown]', shown.length).replace('[total]', count.dataset.total);
      if (empty) empty.hidden = shown.length > 0;
      const filtered = state.fam !== 'all' || state.subj !== 'all';
      clears.forEach((b) => { if (!b.closest('[data-gx-empty]')) b.hidden = !filtered; });
      press(famBtns, 'data-gx-fam', state.fam);
      press(subjBtns, 'data-gx-subj', state.subj);
      press(sortBtns, 'data-gx-sort', state.sort);
    }

    famBtns.forEach((b) => b.addEventListener('click', () => { state.fam = b.getAttribute('data-gx-fam'); apply(); }));
    subjBtns.forEach((b) => b.addEventListener('click', () => { state.subj = b.getAttribute('data-gx-subj'); apply(); }));
    sortBtns.forEach((b) => b.addEventListener('click', () => { state.sort = b.getAttribute('data-gx-sort'); apply(); }));
    clears.forEach((b) => b.addEventListener('click', () => { state.fam = 'all'; state.subj = 'all'; apply(); }));
    if (more) more.addEventListener('click', () => {
      state.all = true;
      const first = cards.find((c) => c.classList.contains('is-extra') && !c.hidden);
      apply();
      if (first) first.focus();
    });
    apply();
  }

  function init(scope) {
    const s = scope || document;
    s.querySelectorAll('[data-gx-spectrum]').forEach((r) => { if (!r.gxInit) { r.gxInit = true; initSpectrum(r); } });
    s.querySelectorAll('[data-gx-grid]').forEach((r) => { if (!r.gxInit) { r.gxInit = true; initGrid(r); } });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
